import debounce from 'just-debounce'
import { uniq } from 'lodash-es'
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface UseFilterSearchOptions {
  botId: string | null | undefined
  tenantId: string | null | undefined
  q: string[]
  waitMs: number
  useAutoSearch: boolean
  useKnowledgeSearch: boolean
  location: { pathname: string }
  onSearch?: (q: string[], page?: number) => void
  callComplete: (options: {
    variables: {
      input: {
        botId: string | null | undefined
        tenantId: string | null | undefined
        keyword: string
        q: string[]
        tags: string[]
      }
    }
  }) => void | Promise<unknown>
  callSuggest: (options: {
    variables: {
      input: {
        botId: string | null | undefined
        tenantId: string | null | undefined
        q: string[]
        tags: string[]
      }
    }
  }) => void | Promise<unknown>
  completeResult?: { complete?: string[] } | null
  suggestResult?: { suggest?: string[] } | null
  navigate: (path: string, options?: { state?: { q: string[]; page: number } }) => void
  getPathname: (location: { pathname: string }) => string
}

export function useFilterSearch({
  botId,
  tenantId,
  q,
  waitMs,
  useAutoSearch,
  useKnowledgeSearch,
  location,
  onSearch,
  callComplete,
  callSuggest,
  completeResult,
  suggestResult,
  navigate,
  getPathname,
}: UseFilterSearchOptions) {
  const [options, setOptions] = useState(completeResult?.complete || [])
  const [keywords, setKeywords] = useState(suggestResult?.suggest || [])
  const inputRef = useRef('')

  const memoizedComplete = useMemo(
    () =>
      debounce(
        (keyword: string) => {
          if (!useKnowledgeSearch) {
            return
          }
          // TODO tagsの量が多いとcloudfrontのリクエスト最大長を超える場合がある。現在、非使用。
          callComplete({ variables: { input: { botId, tenantId, keyword, q, tags: [] } } })
        },
        waitMs,
        false,
        true
      ),
    [botId, tenantId, callComplete, q, waitMs, useKnowledgeSearch]
  )
  const complete = useCallback((keyword: string) => memoizedComplete(keyword), [memoizedComplete])

  useEffect(() => {
    complete('')
    if (!useKnowledgeSearch) {
      return
    }
    // TODO tagsの量が多いとcloudfrontのリクエスト最大長を超える場合がある。現在、非使用。
    callSuggest({ variables: { input: { botId, tenantId, q, tags: [] } } })
  }, [botId, tenantId, callSuggest, complete, q, useKnowledgeSearch])

  useEffect(() => {
    if (suggestResult?.suggest) {
      setKeywords(suggestResult.suggest)
    }
  }, [suggestResult])

  useEffect(() => {
    if (completeResult?.complete) {
      setOptions(completeResult.complete)
    }
  }, [completeResult])

  const handleChange = useCallback(
    (newValue: string[]) => {
      if (onSearch) {
        onSearch(newValue, 1)
        return
      }
      const pathname = getPathname(location)
      navigate(`${pathname}/?q=${newValue.join(' ')}`, { state: { q: newValue, page: 1 } })
    },
    [onSearch, navigate, getPathname, location]
  )

  const handleSearch = useCallback(
    (event?: FormEvent) => {
      if (event) {
        event.preventDefault()
      }
      if (inputRef.current) {
        const newValue = uniq([...q, ...inputRef.current.split(/\s+/).filter(x => x)])
        handleChange(newValue)
        inputRef.current = ''
        return
      }
      if (onSearch) {
        onSearch(q)
        return
      }
      const pathname = getPathname(location)
      navigate(`${pathname}/?q=${q.join(' ')}`, { state: { q, page: 1 } })
    },
    [q, handleChange, onSearch, navigate, getPathname, location]
  )

  useEffect(() => {
    if (canAutoSearch(useAutoSearch, location, q)) {
      handleSearch()
    }
  }, [useAutoSearch, location, q, handleSearch])

  const handleInput = useCallback(
    (value: string) => {
      if (value) {
        complete(value)
      }
      inputRef.current = value
    },
    [complete]
  )

  const handleSelect = useCallback(
    (value: string) => {
      handleChange(uniq([...q, value]))
    },
    [q, handleChange]
  )

  return {
    options,
    keywords,
    handleInput,
    handleChange,
    handleSelect,
    handleSearch,
  }
}

function canAutoSearch(useAutoSearch: boolean, location: { pathname: string }, q: string[]) {
  if (
    !useAutoSearch ||
    (location.pathname !== '/search' && location.pathname !== '/search/' && q.length === 0)
  ) {
    return false
  }
  return true
}
